import * as React from "react";

type IconProps = {
  size?: number;
  fill?: string;
  stroke?: string;
  sw?: number;
  style?: React.CSSProperties;
  className?: string;
};

const Icon = ({
  d,
  size = 20,
  fill = "none",
  stroke = "currentColor",
  sw = 1.75,
  style,
  className,
}: IconProps & { d: React.ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke={stroke}
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
    className={className}
  >
    {typeof d === "string" ? <path d={d} /> : d}
  </svg>
);

const rtlMirror = (className?: string) =>
  ["rtl-mirror", className].filter(Boolean).join(" ");

export const Icons = {
  back: (p: IconProps) => (
    <Icon {...p} className={rtlMirror(p.className)} d="M15 18l-6-6 6-6" />
  ),
  close: (p: IconProps) => <Icon {...p} d="M18 6L6 18M6 6l12 12" />,
  search: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </>
      }
    />
  ),
  filter: (p: IconProps) => <Icon {...p} d="M3 6h18M6 12h12M10 18h4" />,
  cart: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 5h2l2.4 11.4a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.5L21 8H6" />
          <circle cx="9" cy="21" r="1.2" />
          <circle cx="18" cy="21" r="1.2" />
        </>
      }
    />
  ),
  pin: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" />
          <circle cx="12" cy="9" r="2.5" />
        </>
      }
    />
  ),
  clock: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </>
      }
    />
  ),
  star: (p: IconProps) => (
    <Icon
      {...p}
      d="M12 3l2.7 5.7 6.3.9-4.6 4.4 1.1 6.3L12 17.4 6.5 20.3l1.1-6.3L3 9.6l6.3-.9z"
      fill="currentColor"
      sw={0}
    />
  ),
  heart: (p: IconProps) => (
    <Icon
      {...p}
      d="M12 21s-7-4.5-9.3-9.4A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.3 5.6C19 16.5 12 21 12 21z"
    />
  ),
  plus: (p: IconProps) => <Icon {...p} d="M12 5v14M5 12h14" sw={2} />,
  minus: (p: IconProps) => <Icon {...p} d="M5 12h14" sw={2} />,
  check: (p: IconProps) => <Icon {...p} d="M5 12l4.5 4.5L19 7" sw={2.25} />,
  chevron: (p: IconProps) => (
    <Icon {...p} className={rtlMirror(p.className)} d="M9 6l6 6-6 6" />
  ),
  chevronD: (p: IconProps) => <Icon {...p} d="M6 9l6 6 6-6" />,
  user: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c1-4.4 4.5-7 8-7s7 2.6 8 7" />
        </>
      }
    />
  ),
  bag: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M5 8h14l-1.2 12.1a2 2 0 0 1-2 1.9H8.2a2 2 0 0 1-2-1.9L5 8z" />
          <path d="M9 8V6a3 3 0 0 1 6 0v2" />
        </>
      }
    />
  ),
  receipt: (p: IconProps) => <Icon {...p} d="M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6M9 16h4" />,
  bell: (p: IconProps) => <Icon {...p} d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2.5h-15zM10 21h4" />,
  home: (p: IconProps) => <Icon {...p} d="M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10" />,
  spark: (p: IconProps) => (
    <Icon
      {...p}
      d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"
    />
  ),
  bike: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="6" cy="17" r="3.5" />
          <circle cx="18" cy="17" r="3.5" />
          <path d="M6 17l4-9h4l3 9M14 8h3" />
        </>
      }
    />
  ),
  shield: (p: IconProps) => <Icon {...p} d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6z" />,
  tag: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 12V4h8l10 10-8 8z" />
          <circle cx="8" cy="8" r="1.5" />
        </>
      }
    />
  ),
  flame: (p: IconProps) => (
    <Icon {...p} d="M12 2c1 4 5 5 5 10a5 5 0 1 1-10 0c0-2 1-3 2-4 0 2 1 3 2 3 0-3-1-5 1-9z" />
  ),
  card: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <rect x="3" y="6" width="18" height="13" rx="2" />
          <path d="M3 10h18M7 15h3" />
        </>
      }
    />
  ),
  apple: (p: IconProps) => (
    <Icon
      {...p}
      d="M16 13c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.5-.2-2.8.8-3.5.8-.7 0-1.8-.8-3-.8-1.6 0-3 1-3.8 2.4-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.4 1.2 0 1.6-.8 3-.8 1.4 0 1.8.8 3 .8 1.2 0 2-1.1 2.8-2.2.6-.8 1-1.7 1.3-2.6-1.4-.5-2.5-1.9-2.5-4zM14 4.7c.7-.8 1.1-1.9 1-3-1 .1-2.1.7-2.8 1.4-.6.7-1.2 1.8-1 2.9 1.1.1 2.2-.5 2.8-1.3z"
      fill="currentColor"
      sw={0}
    />
  ),
  ticket: (p: IconProps) => (
    <Icon
      {...p}
      d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z"
    />
  ),
  edit: (p: IconProps) => <Icon {...p} d="M4 20h4l10-10-4-4L4 16zM14 6l4 4" />,
  trash: (p: IconProps) => (
    <Icon
      {...p}
      d="M5 7h14M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M7 7l1 13a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-13M10 11v7M14 11v7"
    />
  ),
  info: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8h.01M11 12h1v5h1" />
        </>
      }
    />
  ),
  leaf: (p: IconProps) => (
    <Icon
      {...p}
      d="M21 3c-9 0-15 5-15 12 0 2 1 4 2 5 1-7 6-12 13-13M5 20c4-6 9-9 14-10"
    />
  ),
  store: (p: IconProps) => <Icon {...p} d="M3 9l1.5-5h15L21 9M4 9v11h7v-7h2v7h7V9M4 9h16" />,
  globe: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </>
      }
    />
  ),
  arrowLeft: (p: IconProps) => (
    <Icon {...p} className={rtlMirror(p.className)} d="M19 12H5M12 19l-7-7 7-7" />
  ),
  gift: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </>
      }
    />
  ),
  checkCircle: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M8 12l3 3 5-5" />
        </>
      }
    />
  ),
  loading: (p: IconProps) => (
    <Icon
      {...p}
      d="M12 2a10 10 0 1 0 10 10"
      stroke="currentColor"
      fill="none"
    />
  ),
  /* ── Loyalty expansion (Block 5) — paths match the design handoff's
        primitives.jsx so the shipped screens read identical to the canvas. */
  truck: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <rect x="2" y="7" width="11" height="9" rx="1" />
          <path d="M13 10h4l3 3v3h-7zM6 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
        </>
      }
    />
  ),
  bolt: (p: IconProps) => (
    <Icon {...p} d="M13 3L4 14h7l-1 7 9-11h-7l1-7z" fill="currentColor" sw={1} />
  ),
  chair: (p: IconProps) => (
    <Icon {...p} d="M6 7c0-2 2-3 6-3s6 1 6 3v6H6V7zm0 6l-1 8m14-8l1 8M6 13h12" />
  ),
  cake: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M4 21V12a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9z" />
          <path d="M4 16h16M8 10V7M12 10V5M16 10V7" />
        </>
      }
    />
  ),
  trophy: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M6 3h12v4a6 6 0 0 1-12 0V3z" />
          <path d="M6 5H3v2a3 3 0 0 0 3 3M18 5h3v2a3 3 0 0 1-3 3" />
          <path d="M9 18h6v3H9z M12 13v5" />
        </>
      }
    />
  ),
  fire: (p: IconProps) => (
    <Icon
      {...p}
      d="M12 2c.5 3 4 4.5 4 9a4 4 0 1 1-8 0c0-1.6.8-2.7 1.7-3.5 0 1.6.8 2.5 1.5 2.5 0-2.6-1.2-4.5.8-8z"
      fill="currentColor"
      sw={0}
    />
  ),
  share: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="6" cy="12" r="2.5" />
          <circle cx="18" cy="6" r="2.5" />
          <circle cx="18" cy="18" r="2.5" />
          <path d="M8 11l8-4M8 13l8 4" />
        </>
      }
    />
  ),
  copy: (p: IconProps) => (
    <Icon {...p} d="M8 6h11a2 2 0 0 1 2 2v11M16 4H5a2 2 0 0 0-2 2v11h13a2 2 0 0 0 2-2V4z" />
  ),
  qr: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20v1" />
        </>
      }
    />
  ),
  lock: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </>
      }
    />
  ),
  wallet: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <rect x="3" y="6" width="18" height="14" rx="2" />
          <path d="M3 10h18M16 14h2" />
        </>
      }
    />
  ),
  cal: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </>
      }
    />
  ),
  dish: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 13a9 9 0 0 1 18 0" />
          <path d="M2 13h20M5 17h14" />
        </>
      }
    />
  ),
  google: (p: IconProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M22 12c0-.8-.1-1.6-.2-2.3H12v4.6h5.6c-.2 1.2-.9 2.2-1.9 2.9v2.4h3.1c1.8-1.7 2.9-4.1 2.9-7" />
          <path d="M12 22c2.6 0 4.8-.9 6.4-2.4l-3.1-2.4c-.9.6-2 .9-3.3.9-2.5 0-4.7-1.7-5.4-4H3.4v2.5C5 19.7 8.2 22 12 22z" />
          <path d="M6.6 14.1c-.2-.6-.3-1.3-.3-2.1s.1-1.4.3-2.1V7.4H3.4C2.5 8.8 2 10.4 2 12s.5 3.2 1.4 4.6l3.2-2.5z" />
          <path d="M12 6.5c1.4 0 2.7.5 3.7 1.4l2.8-2.8C16.8 3.7 14.6 2.8 12 2.8 8.2 2.8 5 5.1 3.4 8.4l3.2 2.5C7.3 8.2 9.5 6.5 12 6.5z" />
        </>
      }
      sw={0}
      fill="currentColor"
    />
  ),
};
